import React, { useState, useEffect } from 'react';
import { EmailCampaign, EmailMailbox, EmailTemplate, BranchEventType } from '../../../services/emailService';
import { toast } from 'react-hot-toast';
import TemplateEditor from '../TemplateEditor';
import CampaignPreflightPanel from './CampaignPreflightPanel';
import AIEmailGeneratorModal from './AIEmailGeneratorModal';
import TemplateLibraryModal from './TemplateLibraryModal';
import SendTimeOptimizer from './SendTimeOptimizer';
import PersonalizationAssistant from './PersonalizationAssistant';
import { Sparkles, FileText, Clock, Zap } from 'lucide-react';

// Step indicators for the multi-step process
const STEPS = [
  { id: 'basics', name: 'Basic Info' },
  { id: 'content', name: 'Content' },
  { id: 'sequence', name: 'Sequence' },
  { id: 'schedule', name: 'Schedule' },
  { id: 'review', name: 'Review' }
];

interface MultiStepCampaignModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (campaign: EmailCampaign) => Promise<void>;
  mailboxes: EmailMailbox[];
  templates: EmailTemplate[];
  initialCampaign?: Partial<EmailCampaign>;
}

const MultiStepCampaignModal: React.FC<MultiStepCampaignModalProps> = ({
  isOpen,
  onClose,
  onSave,
  mailboxes,
  templates,
  initialCampaign
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAIGenerator, setShowAIGenerator] = useState(false);
  const [showTemplateLibrary, setShowTemplateLibrary] = useState(false);
  const [showPersonalization, setShowPersonalization] = useState(false);
  const [campaign, setCampaign] = useState<Partial<EmailCampaign>>({
    name: '',
    mailbox_id: '',
    template_id: '',
    is_sequence: false,
    follow_up_steps: [
      { step_number: 1, subject: '', template_id: '', interval_days: 3, is_active: true }
    ],
    schedule_settings: {
      enabled: false,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      send_days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
      send_time_start: '09:00',
      send_time_end: '17:00',
      max_emails_per_day: 50
    },
    enableABTesting: false,
    abTestType: 'subject',
    abTestVariants: [
      { name: 'Control', isControl: true, subject: '', content: '', distribution: 50, templateId: '' },
      { name: 'Variant B', isControl: false, subject: '', content: '', distribution: 50, templateId: '' }
    ],
    winnerMetric: 'open_rate'
  });

  // Initialize campaign with provided data if available
  useEffect(() => {
    if (initialCampaign) {
      setCampaign(prev => ({ ...prev, ...initialCampaign }));
    }
  }, [initialCampaign]);

  // For beginners, if there is exactly one mailbox available and none selected yet,
  // auto-select it so they can move past the basics step without extra clicks.
  useEffect(() => {
    if (!isOpen) return;
    if (mailboxes.length === 1) {
      setCampaign(prev => {
        if (prev.mailbox_id) return prev;
        const onlyMailbox = mailboxes[0];
        return onlyMailbox ? { ...prev, mailbox_id: onlyMailbox.id as string } : prev;
      });
    }
  }, [isOpen, mailboxes]);

  // Handle field changes
  const handleChange = (field: keyof EmailCampaign, value: any) => {
    setCampaign(prev => ({ ...prev, [field]: value }));
  };

  // Handle nested field changes (for complex objects)
  const handleNestedChange = (parent: keyof EmailCampaign, field: string, value: any) => {
    setCampaign(prev => ({
      ...prev,
      [parent]: { ...(prev[parent] as any || {}), [field]: value }
    }));
  };

  // Navigate to next step
  const handleNext = () => {
    if (validateCurrentStep()) {
      setCurrentStep(prev => Math.min(prev + 1, STEPS.length - 1));
    }
  };

  // Navigate to previous step
  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
  };

  // Validate the current step before proceeding
  const validateCurrentStep = (): boolean => {
    switch (STEPS[currentStep].id) {
      case 'basics':
        if (!campaign.name) {
          toast.error('Campaign name is required');
          return false;
        }
        if (mailboxes.length > 0 && !campaign.mailbox_id) {
          toast.error('Please select a mailbox');
          return false;
        }
        return true;
      case 'content':
        {
          const isCreate = !campaign.template_action || campaign.template_action === 'create';
          if (isCreate) {
            if (!campaign.subject) {
              toast.error('Email subject is required');
              return false;
            }
            if (!campaign.new_template_content) {
              toast.error('Email content is required');
              return false;
            }
          } else {
            if (!campaign.template_id) {
              toast.error('Please select an email template');
              return false;
            }
            if (!campaign.subject) {
              toast.error('Email subject is required');
              return false;
            }
          }
          return true;
        }
      case 'sequence':
        // Ensure each step in the sequence has a subject if sequence is enabled
        if (campaign.is_sequence) {
          return (campaign.follow_up_steps || []).every(step => !!step.subject);
        }
        return true;
      case 'schedule':
        return true; // Schedule is optional
      case 'review':
        return true;
      default:
        return true;
    }
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateCurrentStep()) return;

    // Final validation across all critical fields before save
    if (!campaign.name || !campaign.mailbox_id) {
      toast.error('Please complete the Basic Info step (name and mailbox).');
      return;
    }
    const isCreate = !campaign.template_action || campaign.template_action === 'create';
    if (isCreate) {
      if (!campaign.subject || !campaign.new_template_content) {
        toast.error('Please provide an email subject and content in the Content step.');
        return;
      }
    } else {
      if (!campaign.template_id || !campaign.subject) {
        toast.error('Please select a template and ensure a subject is set.');
        return;
      }
    }

    setIsSubmitting(true);
    try {
      await onSave(campaign as EmailCampaign);
      onClose();
    } catch (error) {
      console.error('Error saving campaign:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render step content based on current step
  const renderStepContent = () => {
    switch (STEPS[currentStep].id) {
      case 'basics':
        return (
          <div className="space-y-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                Campaign Name*
              </label>
              <input
                type="text"
                id="name"
                value={campaign.name || ''}
                onChange={(e) => handleChange('name', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            <div>
              <label htmlFor="mailbox_id" className="block text-sm font-medium text-gray-700 mb-1">
                Mailbox
              </label>
              <select
                id="mailbox_id"
                value={campaign.mailbox_id || ''}
                onChange={(e) => handleChange('mailbox_id', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select a mailbox (optional)</option>
                {mailboxes.map((mailbox) => (
                  <option key={mailbox.id} value={mailbox.id}>
                    {mailbox.name} ({mailbox.email})
                  </option>
                ))}
              </select>
            </div>
          </div>
        );

      case 'content':
        return (
          <div className="space-y-4">
            {/* Quick Action Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
              <button
                type="button"
                onClick={() => setShowTemplateLibrary(true)}
                className="p-4 border-2 border-blue-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all text-left group"
              >
                <div className="flex items-center gap-3 mb-2">
                  <FileText className="w-5 h-5 text-blue-600" />
                  <h4 className="font-semibold text-gray-900">Template Library</h4>
                </div>
                <p className="text-xs text-gray-600">15 pre-built templates</p>
              </button>

              <button
                type="button"
                onClick={() => setShowAIGenerator(true)}
                className="p-4 border-2 border-purple-300 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-all text-left group"
              >
                <div className="flex items-center gap-3 mb-2">
                  <Sparkles className="w-5 h-5 text-purple-600" />
                  <h4 className="font-semibold text-gray-900">AI Generator</h4>
                </div>
                <p className="text-xs text-gray-600">Generate from scratch</p>
              </button>

              <button
                type="button"
                onClick={() => setShowPersonalization(true)}
                className="p-4 border-2 border-green-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition-all text-left group"
              >
                <div className="flex items-center gap-3 mb-2">
                  <Zap className="w-5 h-5 text-green-600" />
                  <h4 className="font-semibold text-gray-900">Personalization</h4>
                </div>
                <p className="text-xs text-gray-600">LinkedIn + company data</p>
              </button>
            </div>

            <div className="border-t pt-4">
              <div>
                <label htmlFor="template_action" className="block text-sm font-medium text-gray-700 mb-1">
                  Or Select Existing Template
                </label>
                <select
                  id="template_action"
                  value={campaign.template_action || 'select'}
                  onChange={(e) => {
                    handleChange('template_action', e.target.value);
                    if (e.target.value === 'create') {
                      handleChange('template_id', '');
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="select">Select existing template</option>
                  <option value="create">Create new template</option>
                </select>
              </div>
            </div>

            {(!campaign.template_action || campaign.template_action === 'select') ? (
              <div className="mt-3">
                <select
                  id="template_id"
                  value={campaign.template_id || ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    handleChange('template_id', value);
                    const selected = templates.find(t => t.id === value);
                    if (selected && !campaign.subject) {
                      handleChange('subject', selected.subject);
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select a template</option>
                  {templates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div className="space-y-3 mt-3">
                <div>
                  <label htmlFor="new_template_name" className="block text-sm font-medium text-gray-700 mb-1">
                    New Template Name
                  </label>
                  <input
                    type="text"
                    id="new_template_name"
                    value={campaign.new_template_name || ''}
                    onChange={(e) => handleChange('new_template_name', e.target.value)}
                    placeholder="Enter template name"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label htmlFor="new_template_subject" className="block text-sm font-medium text-gray-700 mb-1">
                    Email Subject
                  </label>
                  <input
                    type="text"
                    id="new_template_subject"
                    value={campaign.subject || ''}
                    onChange={(e) => handleChange('subject', e.target.value)}
                    placeholder="Enter email subject"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label htmlFor="new_template_content" className="block text-sm font-medium text-gray-700 mb-1">
                    Email Content
                  </label>
                  <TemplateEditor
                    subject={campaign.subject || ''}
                    content={campaign.new_template_content || ''}
                    onSubjectChange={(subject: string) => handleChange('subject', subject)}
                    onContentChange={(content: string) => handleChange('new_template_content', content)}
                    className="w-full"
                  />
                </div>
              </div>
            )}
            
            {/* Modals */}
            <TemplateLibraryModal
              isOpen={showTemplateLibrary}
              onClose={() => setShowTemplateLibrary(false)}
              onSelectTemplate={(template) => {
                handleChange('subject', template.subject);
                handleChange('new_template_content', template.body);
                handleChange('template_action', 'create');
                toast.success(`Applied template: ${template.name}`);
                setShowTemplateLibrary(false);
              }}
            />
            
            <AIEmailGeneratorModal
              isOpen={showAIGenerator}
              onClose={() => setShowAIGenerator(false)}
              onUseEmail={(subject, body) => {
                handleChange('subject', subject);
                handleChange('new_template_content', body);
                handleChange('template_action', 'create');
                setShowAIGenerator(false);
              }}
            />

            {showPersonalization && (
              <div className="mt-4">
                <PersonalizationAssistant
                  onUseContent={(subject, body) => {
                    handleChange('subject', subject);
                    handleChange('new_template_content', body);
                    handleChange('template_action', 'create');
                    setShowPersonalization(false);
                  }}
                />
              </div>
            )}
          </div>
        );
      
      case 'sequence':
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-2">
              <label htmlFor="is_sequence" className="block text-sm font-medium text-gray-700">
                Enable Multi-Step Campaign Sequence
              </label>
              <div className="ml-2">
                <label className="inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    id="is_sequence"
                    checked={campaign.is_sequence || false}
                    onChange={(e) => handleChange('is_sequence', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="relative w-11 h-6 bg-gray-200 rounded-full peer peer-focus:ring-4 peer-focus:ring-blue-300 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
            </div>
            
            {campaign.is_sequence && (
              <div className="bg-gray-50 p-4 rounded-md">
                <h4 className="font-medium text-sm mb-3">Follow-up Sequence</h4>
                <div className="space-y-4">
                  {campaign.follow_up_steps?.map((step, index) => (
                    <div key={index} className="border border-gray-300 rounded-md p-3 bg-white">
                      <div className="flex justify-between items-center mb-2">
                        <div className="font-medium text-sm">
                          {index === 0 ? 'Initial Email' : `Follow-up #${index}`}
                        </div>
                      </div>
                      
                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">
                            Subject <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={step.subject || ''}
                            onChange={(e) => {
                              const updatedSteps = [...(campaign.follow_up_steps || [])];
                              updatedSteps[index] = { ...step, subject: e.target.value };
                              handleChange('follow_up_steps', updatedSteps);
                            }}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md"
                            required
                          />
                        </div>
                        
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">
                            Template
                          </label>
                          <div className="space-y-2">
                            <select
                              value={step.template_action || 'select'}
                              onChange={(e) => {
                                const updatedSteps = [...(campaign.follow_up_steps || [])];
                                updatedSteps[index] = { 
                                  ...step, 
                                  template_action: e.target.value as 'select' | 'create',
                                  template_id: e.target.value === 'create' ? '' : step.template_id 
                                };
                                handleChange('follow_up_steps', updatedSteps);
                              }}
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md"
                            >
                              <option value="select">Select existing template</option>
                              <option value="create">Create new template</option>
                            </select>
                            
                            {(!step.template_action || step.template_action === 'select') ? (
                              <select
                                value={step.template_id || ''}
                                onChange={(e) => {
                                  const updatedSteps = [...(campaign.follow_up_steps || [])];
                                  updatedSteps[index] = { ...step, template_id: e.target.value };
                                  handleChange('follow_up_steps', updatedSteps);
                                }}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md mt-2"
                              >
                                <option value="">Select a template</option>
                                {templates.map((template) => (
                                  <option key={template.id} value={template.id}>
                                    {template.name}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <div className="space-y-2 mt-2 p-2 bg-gray-50 rounded-md">
                                <div>
                                  <label className="block text-xs font-medium text-gray-500 mb-1">
                                    New Template Name
                                  </label>
                                  <input
                                    type="text"
                                    value={step.new_template_name || ''}
                                    onChange={(e) => {
                                      const updatedSteps = [...(campaign.follow_up_steps || [])];
                                      updatedSteps[index] = { ...step, new_template_name: e.target.value };
                                      handleChange('follow_up_steps', updatedSteps);
                                    }}
                                    placeholder="Enter template name"
                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-500 mb-1">
                                    Template Content
                                  </label>
                                  <textarea
                                    rows={3}
                                    value={step.new_template_content || ''}
                                    onChange={(e) => {
                                      const updatedSteps = [...(campaign.follow_up_steps || [])];
                                      updatedSteps[index] = { ...step, new_template_content: e.target.value };
                                      handleChange('follow_up_steps', updatedSteps);
                                    }}
                                    placeholder="Enter email content"
                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md"
                                  ></textarea>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {index > 0 && (
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">
                              Days after previous email
                            </label>
                            <input
                              type="number"
                              min="1"
                              max="30"
                              value={step.interval_days || 3}
                              onChange={(e) => {
                                const updatedSteps = [...(campaign.follow_up_steps || [])];
                                updatedSteps[index] = { ...step, interval_days: parseInt(e.target.value) || 3 };
                                handleChange('follow_up_steps', updatedSteps);
                              }}
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md"
                            />
                          </div>
                        )}

                        <div className="border-t pt-3 space-y-3 mt-2">
                          <div>
                            <div className="flex items-center justify-between mb-1">
                              <span className="block text-xs font-medium text-gray-500">Step-level A/B variants</span>
                              <button
                                type="button"
                                onClick={() => {
                                  const updatedSteps = [...(campaign.follow_up_steps || [])];
                                  const existing = step.variants || [];
                                  const nextIndex = existing.length + 1;
                                  const newVariant = {
                                    name: `Variant ${nextIndex}`,
                                    isControl: existing.length === 0,
                                    subject: '',
                                    distribution: 0
                                  };
                                  updatedSteps[index] = {
                                    ...step,
                                    variants: [...existing, newVariant]
                                  };
                                  handleChange('follow_up_steps', updatedSteps);
                                }}
                                className="text-xs text-blue-600 hover:text-blue-700"
                              >
                                + Add variant
                              </button>
                            </div>
                            {step.variants && step.variants.length > 0 && (
                              <div className="space-y-2">
                                {step.variants.map((variant, vIndex) => (
                                  <div key={vIndex} className="grid grid-cols-1 sm:grid-cols-4 gap-2 items-center">
                                    <input
                                      type="text"
                                      value={variant.name || ''}
                                      onChange={(e) => {
                                        const updatedSteps = [...(campaign.follow_up_steps || [])];
                                        const variants = [...(step.variants || [])];
                                        variants[vIndex] = { ...variants[vIndex], name: e.target.value };
                                        updatedSteps[index] = { ...step, variants };
                                        handleChange('follow_up_steps', updatedSteps);
                                      }}
                                      placeholder="Name"
                                      className="px-2 py-1 text-xs border border-gray-300 rounded-md"
                                    />
                                    <input
                                      type="text"
                                      value={variant.subject || ''}
                                      onChange={(e) => {
                                        const updatedSteps = [...(campaign.follow_up_steps || [])];
                                        const variants = [...(step.variants || [])];
                                        variants[vIndex] = { ...variants[vIndex], subject: e.target.value };
                                        updatedSteps[index] = { ...step, variants };
                                        handleChange('follow_up_steps', updatedSteps);
                                      }}
                                      placeholder="Subject override (optional)"
                                      className="px-2 py-1 text-xs border border-gray-300 rounded-md"
                                    />
                                    <div className="flex items-center gap-2">
                                      <input
                                        type="number"
                                        min="0"
                                        max="100"
                                        value={typeof variant.distribution === 'number' ? variant.distribution : 0}
                                        onChange={(e) => {
                                          const value = parseInt(e.target.value) || 0;
                                          const updatedSteps = [...(campaign.follow_up_steps || [])];
                                          const variants = [...(step.variants || [])];
                                          variants[vIndex] = { ...variants[vIndex], distribution: value };
                                          updatedSteps[index] = { ...step, variants };
                                          handleChange('follow_up_steps', updatedSteps);
                                        }}
                                        className="w-16 px-2 py-1 text-xs border border-gray-300 rounded-md"
                                      />
                                      <span className="text-xs text-gray-500">%</span>
                                      <label className="inline-flex items-center text-xs text-gray-600">
                                        <input
                                          type="checkbox"
                                          checked={variant.isControl === true}
                                          onChange={(e) => {
                                            const updatedSteps = [...(campaign.follow_up_steps || [])];
                                            const variants = (step.variants || []).map((v, idx) => ({
                                              ...v,
                                              isControl: idx === vIndex ? e.target.checked : v.isControl
                                            }));
                                            updatedSteps[index] = { ...step, variants };
                                            handleChange('follow_up_steps', updatedSteps);
                                          }}
                                          className="h-3 w-3 mr-1"
                                        />
                                        Control
                                      </label>
                                    </div>
                                    <div className="flex items-center justify-between gap-2">
                                      <input
                                        type="number"
                                        min="0"
                                        value={typeof variant.autoWinnerThreshold === 'number' ? variant.autoWinnerThreshold : ''}
                                        onChange={(e) => {
                                          const value = e.target.value === '' ? undefined : parseInt(e.target.value) || 0;
                                          const updatedSteps = [...(campaign.follow_up_steps || [])];
                                          const variants = [...(step.variants || [])];
                                          variants[vIndex] = { ...variants[vIndex], autoWinnerThreshold: value };
                                          updatedSteps[index] = { ...step, variants };
                                          handleChange('follow_up_steps', updatedSteps);
                                        }}
                                        placeholder="Auto-winner sends"
                                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded-md"
                                      />
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const updatedSteps = [...(campaign.follow_up_steps || [])];
                                          const variants = [...(step.variants || [])];
                                          variants.splice(vIndex, 1);
                                          updatedSteps[index] = { ...step, variants };
                                          handleChange('follow_up_steps', updatedSteps);
                                        }}
                                        className="text-xs text-red-600 hover:text-red-700 ml-2"
                                      >
                                        Remove
                                      </button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>

                          <div>
                            <div className="flex items-center justify-between mb-1 mt-2">
                              <span className="block text-xs font-medium text-gray-500">Branching rules</span>
                              <button
                                type="button"
                                onClick={() => {
                                  const updatedSteps = [...(campaign.follow_up_steps || [])];
                                  const existing = step.branches || [];
                                  const nextIndex = existing.length + 1;
                                  const newBranch = {
                                    name: `Rule ${nextIndex}`,
                                    priority: 10,
                                    isDefault: false,
                                    condition: { event: 'reply' as const },
                                    action: { type: 'exit_sequence' as const }
                                  };
                                  updatedSteps[index] = {
                                    ...step,
                                    branches: [...existing, newBranch]
                                  };
                                  handleChange('follow_up_steps', updatedSteps);
                                }}
                                className="text-xs text-blue-600 hover:text-blue-700"
                              >
                                + Add rule
                              </button>
                            </div>
                            {step.branches && step.branches.length > 0 && (
                              <div className="space-y-2">
                                {step.branches.map((branch, bIndex) => (
                                  <div key={bIndex} className="grid grid-cols-1 sm:grid-cols-4 gap-2 items-center">
                                    <input
                                      type="text"
                                      value={branch.name || ''}
                                      onChange={(e) => {
                                        const updatedSteps = [...(campaign.follow_up_steps || [])];
                                        const branches = [...(step.branches || [])];
                                        branches[bIndex] = { ...branches[bIndex], name: e.target.value };
                                        updatedSteps[index] = { ...step, branches };
                                        handleChange('follow_up_steps', updatedSteps);
                                      }}
                                      placeholder="Rule name"
                                      className="px-2 py-1 text-xs border border-gray-300 rounded-md"
                                    />
                                    <div className="flex items-center gap-2">
                                      <select
                                        value={branch.condition?.event || 'reply'}
                                        onChange={(e) => {
                                          const updatedSteps = [...(campaign.follow_up_steps || [])];
                                          const branches = [...(step.branches || [])];
                                          const event = e.target.value as BranchEventType;
                                          branches[bIndex] = {
                                            ...branches[bIndex],
                                            condition: {
                                              ...(branches[bIndex].condition || {}),
                                              event
                                            }
                                          };
                                          updatedSteps[index] = { ...step, branches };
                                          handleChange('follow_up_steps', updatedSteps);
                                        }}
                                        className="px-2 py-1 text-xs border border-gray-300 rounded-md"
                                      >
                                        <option value="send">Send</option>
                                        <option value="open">Open</option>
                                        <option value="click">Click</option>
                                        <option value="reply">Reply</option>
                                        <option value="bounce">Bounce</option>
                                        <option value="no_event">No event</option>
                                      </select>
                                      <input
                                        type="number"
                                        min="0"
                                        value={typeof branch.condition?.withinDays === 'number' ? branch.condition.withinDays : ''}
                                        onChange={(e) => {
                                          const value = e.target.value === '' ? undefined : parseInt(e.target.value) || 0;
                                          const updatedSteps = [...(campaign.follow_up_steps || [])];
                                          const branches = [...(step.branches || [])];
                                          branches[bIndex] = {
                                            ...branches[bIndex],
                                            condition: {
                                              ...(branches[bIndex].condition || {}),
                                              withinDays: value
                                            }
                                          };
                                          updatedSteps[index] = { ...step, branches };
                                          handleChange('follow_up_steps', updatedSteps);
                                        }}
                                        placeholder="Days"
                                        className="w-20 px-2 py-1 text-xs border border-gray-300 rounded-md"
                                      />
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <select
                                        value={branch.action?.type || 'exit_sequence'}
                                        onChange={(e) => {
                                          const updatedSteps = [...(campaign.follow_up_steps || [])];
                                          const branches = [...(step.branches || [])];
                                          branches[bIndex] = {
                                            ...branches[bIndex],
                                            action: {
                                              type: e.target.value as 'move_to_step' | 'exit_sequence',
                                              targetStepNumber: e.target.value === 'move_to_step'
                                                ? (branches[bIndex].action?.targetStepNumber || step.step_number + 1)
                                                : undefined
                                            }
                                          };
                                          updatedSteps[index] = { ...step, branches };
                                          handleChange('follow_up_steps', updatedSteps);
                                        }}
                                        className="px-2 py-1 text-xs border border-gray-300 rounded-md"
                                      >
                                        <option value="exit_sequence">Exit sequence</option>
                                        <option value="move_to_step">Move to step</option>
                                      </select>
                                      {branch.action?.type === 'move_to_step' && (
                                        <input
                                          type="number"
                                          min="1"
                                          value={typeof branch.action?.targetStepNumber === 'number' ? branch.action.targetStepNumber : ''}
                                          onChange={(e) => {
                                            const value = e.target.value === '' ? undefined : parseInt(e.target.value) || 1;
                                            const updatedSteps = [...(campaign.follow_up_steps || [])];
                                            const branches = [...(step.branches || [])];
                                            branches[bIndex] = {
                                              ...branches[bIndex],
                                              action: {
                                                ...(branches[bIndex].action || { type: 'move_to_step' as const }),
                                                targetStepNumber: value
                                              }
                                            };
                                            updatedSteps[index] = { ...step, branches };
                                            handleChange('follow_up_steps', updatedSteps);
                                          }}
                                          placeholder="Step #"
                                          className="w-20 px-2 py-1 text-xs border border-gray-300 rounded-md"
                                        />
                                      )}
                                    </div>
                                    <div className="flex items-center justify-between gap-2">
                                      <input
                                        type="number"
                                        min="0"
                                        value={typeof branch.priority === 'number' ? branch.priority : 10}
                                        onChange={(e) => {
                                          const value = parseInt(e.target.value) || 10;
                                          const updatedSteps = [...(campaign.follow_up_steps || [])];
                                          const branches = [...(step.branches || [])];
                                          branches[bIndex] = { ...branches[bIndex], priority: value };
                                          updatedSteps[index] = { ...step, branches };
                                          handleChange('follow_up_steps', updatedSteps);
                                        }}
                                        className="w-20 px-2 py-1 text-xs border border-gray-300 rounded-md"
                                      />
                                      <label className="inline-flex items-center text-xs text-gray-600">
                                        <input
                                          type="checkbox"
                                          checked={branch.isDefault === true}
                                          onChange={(e) => {
                                            const updatedSteps = [...(campaign.follow_up_steps || [])];
                                            const branches = (step.branches || []).map((b, idx) => ({
                                              ...b,
                                              isDefault: idx === bIndex ? e.target.checked : false
                                            }));
                                            updatedSteps[index] = { ...step, branches };
                                            handleChange('follow_up_steps', updatedSteps);
                                          }}
                                          className="h-3 w-3 mr-1"
                                        />
                                        Default
                                      </label>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const updatedSteps = [...(campaign.follow_up_steps || [])];
                                          const branches = [...(step.branches || [])];
                                          branches.splice(bIndex, 1);
                                          updatedSteps[index] = { ...step, branches };
                                          handleChange('follow_up_steps', updatedSteps);
                                        }}
                                        className="text-xs text-red-600 hover:text-red-700 ml-2"
                                      >
                                        Remove
                                      </button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  <button
                    type="button"
                    onClick={() => {
                      const lastStep = campaign.follow_up_steps?.[campaign.follow_up_steps.length - 1];
                      const newStep = {
                        step_number: (lastStep?.step_number || 0) + 1,
                        subject: '',
                        template_id: '',
                        interval_days: 3,
                        is_active: true
                      };
                      handleChange('follow_up_steps', [...(campaign.follow_up_steps || []), newStep]);
                    }}
                    className="w-full py-2 px-3 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    + Add Follow-up Step
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      
      case 'schedule':
        return (
          <div className="space-y-6">
            {/* Send Time Optimizer */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <Clock className="w-5 h-5 text-blue-600" />
                <h3 className="font-semibold text-gray-900">Optimal Send Time</h3>
              </div>
              <SendTimeOptimizer
                onSelectTime={(day, hour) => {
                  // Update schedule settings with optimal time
                  handleNestedChange('schedule_settings', 'enabled', true);
                  handleNestedChange('schedule_settings', 'send_time_start', `${hour.toString().padStart(2, '0')}:00`);
                  handleNestedChange('schedule_settings', 'send_time_end', `${(hour + 2).toString().padStart(2, '0')}:00`);
                  toast.success(`Set to send on ${day} at ${hour}:00`);
                }}
              />
            </div>

            <div className="border-t pt-6">
              <div className="flex items-center justify-between mb-2">
                <label htmlFor="schedule_enabled" className="block text-sm font-medium text-gray-700">
                  Custom Advanced Scheduling
                </label>
                <div className="ml-2">
                <label className="inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    id="schedule_enabled"
                    checked={campaign.schedule_settings?.enabled || false}
                    onChange={(e) => handleNestedChange('schedule_settings', 'enabled', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="relative w-11 h-6 bg-gray-200 rounded-full peer peer-focus:ring-4 peer-focus:ring-blue-300 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
                </div>
              </div>
            
              {campaign.schedule_settings?.enabled && (
              <div className="bg-gray-50 p-4 rounded-md space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Timezone
                  </label>
                  <select
                    value={campaign.schedule_settings?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone}
                    onChange={(e) => handleNestedChange('schedule_settings', 'timezone', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  >
                    <option value="UTC">UTC</option>
                    <option value="America/New_York">Eastern Time (ET)</option>
                    <option value="America/Chicago">Central Time (CT)</option>
                    <option value="America/Denver">Mountain Time (MT)</option>
                    <option value="America/Los_Angeles">Pacific Time (PT)</option>
                    <option value="Europe/London">London (GMT)</option>
                    <option value="Europe/Paris">Central European Time (CET)</option>
                    <option value="Asia/Tokyo">Japan (JST)</option>
                    <option value="Australia/Sydney">Sydney (AEST)</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Send Days
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => (
                      <label key={day} className="inline-flex items-center">
                        <input
                          type="checkbox"
                          checked={(campaign.schedule_settings?.send_days || []).includes(day)}
                          onChange={(e) => {
                            const currentDays = campaign.schedule_settings?.send_days || [];
                            const updatedDays = e.target.checked
                              ? [...currentDays, day]
                              : currentDays.filter(d => d !== day);
                            handleNestedChange('schedule_settings', 'send_days', updatedDays);
                          }}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700 capitalize">{day}</span>
                      </label>
                    ))}
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Start Time
                    </label>
                    <input
                      type="time"
                      value={campaign.schedule_settings?.send_time_start || '09:00'}
                      onChange={(e) => handleNestedChange('schedule_settings', 'send_time_start', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      End Time
                    </label>
                    <input
                      type="time"
                      value={campaign.schedule_settings?.send_time_end || '17:00'}
                      onChange={(e) => handleNestedChange('schedule_settings', 'send_time_end', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Maximum Emails Per Day
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="500"
                    value={campaign.schedule_settings?.max_emails_per_day || 50}
                    onChange={(e) => handleNestedChange('schedule_settings', 'max_emails_per_day', parseInt(e.target.value) || 50)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
              </div>
              )}
            </div>
          </div>
        );
      
      case 'review':
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Campaign Summary</h3>
            
            <div className="bg-gray-50 p-4 rounded-md space-y-3">
              <div>
                <span className="text-sm font-medium text-gray-500">Campaign Name:</span>
                <p className="text-sm text-gray-900">{campaign.name}</p>
              </div>
              
              <div>
                <span className="text-sm font-medium text-gray-500">Email Subject:</span>
                <p className="text-sm text-gray-900">{campaign.subject}</p>
              </div>
              
              <div>
                <span className="text-sm font-medium text-gray-500">Mailbox:</span>
                <p className="text-sm text-gray-900">
                  {campaign.mailbox_id 
                    ? mailboxes.find(m => m.id === campaign.mailbox_id)?.name || 'Unknown mailbox'
                    : 'No mailbox selected'}
                </p>
              </div>
              
              <div>
                <span className="text-sm font-medium text-gray-500">Template:</span>
                <p className="text-sm text-gray-900">
                  {campaign.template_id 
                    ? templates.find(t => t.id === campaign.template_id)?.name || 'Unknown template'
                    : 'No template selected'}
                </p>
              </div>
              
              {campaign.is_sequence && (
                <div>
                  <span className="text-sm font-medium text-gray-500">Follow-up Sequence:</span>
                  <p className="text-sm text-gray-900">
                    {campaign.follow_up_steps?.length || 0} step(s)
                  </p>
                </div>
              )}
              
              {campaign.schedule_settings?.enabled && (
                <div>
                  <span className="text-sm font-medium text-gray-500">Scheduling:</span>
                  <p className="text-sm text-gray-900">
                    {campaign.schedule_settings.send_days?.map(d => d.charAt(0).toUpperCase() + d.slice(1)).join(', ')} 
                    from {campaign.schedule_settings.send_time_start} to {campaign.schedule_settings.send_time_end}
                  </p>
                </div>
              )}
            </div>

            {/* Preflight checks (only for existing campaigns with id) */}
            {campaign.id ? (
              <div className="mt-4">
                <CampaignPreflightPanel campaignId={campaign.id as string} />
              </div>
            ) : (
              <div className="mt-4 p-3 rounded border border-gray-200 text-xs text-gray-500">
                Preflight checks are available after the campaign is created.
              </div>
            )}
          </div>
        );
      
      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-0 border w-full max-w-2xl shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center p-4 border-b">
          <h3 className="text-xl font-medium text-gray-900">Create Campaign</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {/* Step Indicator */}
        <div className="px-6 py-4 bg-gray-50 border-b">
          <nav aria-label="Progress">
            <ol className="flex justify-between items-center w-full">
              {STEPS.map((step, index) => (
                <li key={step.id} className="flex flex-col items-center">
                  <div className="flex items-center">
                    <div
                      className={`${
                        index <= currentStep ? 'bg-blue-600' : 'bg-gray-300'
                      } h-8 w-8 rounded-full flex items-center justify-center`}
                      style={{ textDecoration: 'none' }}
                    >
                      <span className="text-white text-sm" style={{ textDecoration: 'none' }}>{index + 1}</span>
                    </div>
                    <span
                      className={`ml-2 text-xs sm:text-sm font-medium ${
                        index <= currentStep ? 'text-blue-600' : 'text-gray-500'
                      } hidden sm:inline`}
                      style={{ textDecoration: 'none' }}
                    >
                      {step.name}
                    </span>
                  </div>
                  {index < STEPS.length - 1 && (
                    <div className="hidden sm:block absolute">
                      <div className="h-0.5 bg-gray-300 w-12 sm:w-24 relative" style={{ left: '2rem' }}>
                        <div
                          className="h-0.5 bg-blue-600 absolute"
                          style={{ width: index < currentStep ? '100%' : '0%', textDecoration: 'none' }}
                        ></div>
                      </div>
                    </div>
                  )}
                </li>
              ))}
            </ol>
          </nav>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="p-6 min-h-[300px]">
            {renderStepContent()}
          </div>
          
          <div className="flex justify-between p-4 bg-gray-50 border-t">
            <button
              type="button"
              onClick={currentStep === 0 ? onClose : handlePrevious}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              {currentStep === 0 ? 'Cancel' : 'Back'}
            </button>
            
            <div className="flex space-x-2">
              {currentStep < STEPS.length - 1 ? (
                <button
                  type="button"
                  onClick={handleNext}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Next
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-400 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline-block" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Creating...
                    </>
                  ) : (
                    'Create Campaign'
                  )}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MultiStepCampaignModal;
